import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, FadeUp, SectionHeading, Counter } from "@/components/Reveal";
import TiltCard from "@/components/TiltCard";
import FounderSection from "@/components/FounderSection";
import useSeo from "@/hooks/useSeo";

const PRINCIPLES = [
  { n: "01", hindi: "ज़मीन", title: "Ground before glamour", desc: "A reel never won a booth. Every plan starts with the voter list and ends at the polling station." },
  { n: "02", hindi: "भाषा", title: "Speak the voter's language", desc: "Hindi, Malwi, Nimadi, Bundeli, Gondi. If the message needs translation, it isn't the message." },
  { n: "03", hindi: "गति", title: "Speed is strategy", desc: "News cycles in India last four hours. Our war room answers in one." },
];

const STATS = [
  { to: 40, suffix: "+", label: "Campaigns run" },
  { to: 6, suffix: "", label: "States" },
  { to: 50, suffix: "+", label: "Districts covered" },
  { to: 12, suffix: "Cr+", label: "Voter impressions" },
];

const TIMELINE = [
  { year: "2019", text: "Anuj Tiwari founds ONWARD MEDIA in Indore as a two-person video and social media unit for local leaders." },
  { year: "2020", text: "First Indore Nagar Nigam campaign. Built our door-to-door tracking app during lockdown." },
  { year: "2022", text: "Statewide IEC campaign for a government department — 50+ districts, four languages." },
  { year: "2023", text: "Vidhan Sabha season: twelve campaigns for BJP and Congress candidates, dedicated war rooms, chunavi geet album." },
  { year: "2024", text: "First Lok Sabha digital war room. Expanded to Rajasthan, Chhattisgarh and UP." },
  { year: "2026", text: "Fifty-plus campaigns in. Bigger office in Vijay Nagar, same hunger." },
];

export default function About() {
  useSeo("About — Anuj Tiwari & Team", "ONWARD MEDIA is an Indore-based political strategy and creative agency founded by Anuj Tiwari. Fifty-plus campaigns across six states.");
  return (
    <div data-testid="about-page" className="mx-auto max-w-[1440px] px-6 pb-24 pt-36 lg:px-12 lg:pt-44">
      <p className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-saffron">About — Indore, Madhya Pradesh</p>
      <h1 className="mt-5 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight text-paper sm:text-6xl lg:text-7xl">
        <MaskedLines inView={false} delay={0.1} lines={["Dil se", "Bharat."]} />
      </h1>

      <FadeUp delay={0.3}>
        <p className="mt-10 max-w-3xl font-script text-2xl italic leading-snug text-zinc-300 sm:text-3xl lg:text-4xl">
          "Most agencies make noise in Delhi and Mumbai. We win mandates in Mhow, Dewas and Dhar —
          and bring that ground truth to every brand we touch."
        </p>
      </FadeUp>

      <FadeUp delay={0.15}>
        <div className="mt-16 grid max-w-4xl grid-cols-2 gap-8 border-t border-white/10 pt-12 sm:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.label} data-testid={`about-page-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <Counter to={s.to} suffix={s.suffix} className="font-display text-3xl font-bold text-saffron sm:text-4xl" />
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{s.label}</p>
            </div>
          ))}
        </div>
      </FadeUp>

      <div className="mt-24 grid grid-cols-1 gap-6 md:grid-cols-12 lg:mt-32 lg:gap-8">
        <FadeUp className="md:col-span-7">
          <div className="overflow-hidden rounded-xl border border-white/10">
            <img
              src="https://images.unsplash.com/photo-1632999876211-813cdb357f37?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxJbmRpYSUyMHBvbGl0aWNhbCUyMHJhbGx5JTIwY3Jvd2QlMjBmbGFnc3xlbnwwfHx8fDE3OTEwNTQxNzF8MA&ixlib=rb-4.1.0&q=85"
              alt="On the ground at a campaign"
              className="aspect-[16/10] w-full object-cover transition-transform duration-700 hover:scale-105"
            />
          </div>
        </FadeUp>
        <FadeUp className="md:col-span-5" delay={0.1}>
          <div className="flex h-full flex-col justify-between gap-6">
            <div className="overflow-hidden rounded-xl border border-white/10">
              <img
                src="https://images.unsplash.com/photo-1573871261617-cb561c240085?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxJbmRpYW4lMjBlbGVjdGlvbiUyMGNhbXBhaWduJTIwbWVkaWElMjBwcmVzcyUyMG1pY3JvcGhvbmV8ZW58MHx8fHwxNzkxMDU0MTgyfDA&ixlib=rb-4.1.0&q=85"
                alt="Leader addressing a nukkad sabha"
                className="aspect-[16/11] w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
            <p className="text-sm leading-relaxed text-zinc-400">
              A forty-person team of strategists, data analysts, filmmakers, editors and ground organisers
              working from Vijay Nagar, Indore, with field coordinators across Madhya Pradesh. No Delhi
              account managers, no hand-offs. The people you meet in the first meeting are the people in
              the war room at 2 a.m.
            </p>
          </div>
        </FadeUp>
      </div>

      <div className="-mx-6 lg:-mx-12">
        <FounderSection compact />
      </div>

      <div className="mt-8 lg:mt-12">
        <SectionHeading overline="Journey" lines={["Seven years,", "one direction."]} testId="about-timeline-heading" />
        <div className="mt-14 grid grid-cols-1 gap-x-10 gap-y-8 md:grid-cols-2 lg:grid-cols-3">
          {TIMELINE.map((t, i) => (
            <FadeUp key={t.year} delay={i * 0.06}>
              <div data-testid={`timeline-${t.year}`} className="border-l border-white/10 pl-6 transition-colors duration-500 hover:border-saffron">
                <p className="font-display text-3xl font-bold text-saffron">{t.year}</p>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{t.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>

      <div className="mt-24 lg:mt-32">
        <SectionHeading overline="Principles" lines={["Three rules", "we never break."]} testId="about-principles-heading" />
        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {PRINCIPLES.map((p, i) => (
            <FadeUp key={p.n} delay={i * 0.08}>
              <TiltCard testId={`principle-${p.n}`}>
                <div className="group h-full rounded-xl border border-white/10 bg-coal/60 p-8 transition-colors duration-500 hover:border-saffron/40">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm text-brand">{p.n}</span>
                    <span className="font-hindi text-xl text-saffron">{p.hindi}</span>
                  </div>
                  <h3 className="mt-6 font-display text-xl font-bold uppercase tracking-tight text-paper">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-500">{p.desc}</p>
                </div>
              </TiltCard>
            </FadeUp>
          ))}
        </div>
      </div>

      <FadeUp className="mt-24 text-center lg:mt-32">
        <p className="font-hindi text-2xl text-saffron">चलिए, साथ चलते हैं।</p>
        <h2 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-paper sm:text-3xl lg:text-4xl">
          Sound like your kind of people?
        </h2>
        <Link
          to="/contact"
          data-testid="about-cta-button"
          className="group mt-8 inline-flex items-center gap-2 rounded-full btn-ribbon px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.15em] text-white transition-colors duration-300 "
        >
          Plan a campaign
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </FadeUp>
    </div>
  );
}
